/**
 * rtDvhModule.js — Этап 5: DVH (гистограмма доза–объём) по структуре RTSTRUCT.
 *
 * Ядро (rtDvhCore) проверено автономными тестами: маска структуры из контуров
 * (объём совпадает со сферой), кумулятивная кривая, Dmax/Dmean/D95/V100.
 * Здесь — связь с загруженными структурами и дозой + отрисовка кривой.
 */

import { log } from '../utils/logger.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { getStructuresRaw } from './rtStructModule.js';
import { getDose } from './rtDoseModule.js';
import { buildStructureMask, computeDVH } from './rtDvhCore.js';

/** DVH по структуре (roiNumber) при назначенной дозе rxGy. */
export function computeDvhForStructure(roiNumber, rxGy) {
  const dose = getDose();
  if (!dose) throw new Error('RTDOSE не загружен');
  const list = getStructuresRaw().filter(s => s.contours && s.contours.length);
  const st = (roiNumber != null ? list.find(s => s.roiNumber === roiNumber) : list[0]);
  if (!st) throw new Error('Нет структуры с контурами');
  const m = buildStructureMask(dose, st.contours);
  const dvh = computeDVH(dose.grid, m.mask, m.voxelVolCc, { prescriptionGy: rxGy || null });
  if (m.skipped) {
    log.warn(`[DVH] ${st.name}: учтено ${m.used} контуров одной плоскости, `
      + `отброшено ${m.skipped} в других проекциях.`);
  }
  return {
    name: st.name, roiNumber: st.roiNumber, structVolCc: m.structVolCc,
    used: m.used, skipped: m.skipped, planes: m.planes, sliceThicknessMm: m.sliceThicknessMm,
    ...dvh,
  };
}

// ── отрисовка кривой (SVG) ───────────────────────────────────────────────────
function _plot(res, color = '#7fe') {
  const W = 280, H = 170, ml = 34, mb = 22, mt = 8, mr = 8;
  const pw = W - ml - mr, ph = H - mt - mb;
  const dmax = res.stats.Dmax || 1;
  const X = gy => ml + pw * (gy / dmax);
  const Y = pct => mt + ph * (1 - pct / 100);
  let path = '';
  res.cumulative.forEach((p, i) => { path += (i ? 'L' : 'M') + X(p.gy).toFixed(1) + ' ' + Y(p.volPct).toFixed(1) + ' '; });
  const gx = []; for (let g = 0; g <= dmax + 1e-6; g += dmax / 4) gx.push(g);
  const gy = [0, 25, 50, 75, 100];
  const axis = gx.map(g => `<line x1="${X(g)}" y1="${Y(0)}" x2="${X(g)}" y2="${mt}" stroke="#2a3644" stroke-width="0.5"/>`
    + `<text x="${X(g)}" y="${H - 8}" fill="#89a" font-size="8" text-anchor="middle">${g.toFixed(0)}</text>`).join('')
    + gy.map(v => `<line x1="${ml}" y1="${Y(v)}" x2="${W - mr}" y2="${Y(v)}" stroke="#2a3644" stroke-width="0.5"/>`
      + `<text x="${ml - 4}" y="${Y(v) + 3}" fill="#89a" font-size="8" text-anchor="end">${v}</text>`).join('');
  const rxLine = res.stats.prescriptionGy
    ? `<line x1="${X(res.stats.prescriptionGy)}" y1="${mt}" x2="${X(res.stats.prescriptionGy)}" y2="${Y(0)}" stroke="#f96" stroke-width="1" stroke-dasharray="3 2"/>` : '';
  return `<svg width="${W}" height="${H}" style="background:#0b1119;border-radius:6px">
    ${axis}${rxLine}
    <path d="${path}" fill="none" stroke="${color}" stroke-width="1.8"/>
    <text x="${ml + pw / 2}" y="${H - 1}" fill="#89a" font-size="8" text-anchor="middle">доза, Гр</text>
    <text x="9" y="${mt + ph / 2}" fill="#89a" font-size="8" text-anchor="middle" transform="rotate(-90 9 ${mt + ph / 2})">объём, %</text>
  </svg>`;
}

function _statsHtml(res) {
  const s = res.stats, g = v => (v == null ? '—' : v.toFixed(2) + ' Гр');
  // Если часть контуров обведена в других проекциях, DVH считается только по
  // главной плоскости. Молчать об этом нельзя: цифры выглядят правдоподобно,
  // а объём при этом занижен в разы.
  const warn = res.skipped
    ? `<div style="margin-top:6px;padding:6px;border-radius:6px;background:#3a2a10;
         border:1px solid #a72;color:#fd8;font-size:10px;line-height:1.5">
         ⚠️ Учтено <b>${res.used}</b> контуров одной плоскости, отброшено <b>${res.skipped}</b>
         в других проекциях. DVH и объём считаются по стопке параллельных срезов —
         контуры из трёх проекций для этого не годятся. Обведите мишень
         последовательно на соседних срезах ОДНОЙ проекции.</div>`
    : '';
  return `<div style="font-size:11px;line-height:1.6;margin-top:4px">
    <b>${res.name}</b> · объём ${res.structVolCc.toFixed(2)} cc
    ${res.planes ? `· срезов ${res.planes}` : ''}<br>
    Dmax ${g(s.Dmax)} · Dmin ${g(s.Dmin)} · Dср ${g(s.Dmean)}<br>
    D95 ${g(s.D95)} · D98 ${g(s.D98)} · D2 ${g(s.D2)}
    ${s.prescriptionGy ? `<br>Покрытие V100 <b>${(s.V100pct ?? 0).toFixed(1)}%</b> (назнач. ${s.prescriptionGy} Гр)` : ''}
  </div>${warn}`;
}

// ── UI ──────────────────────────────────────────────────────────────────────
export function mountDvhUI() {
  if (document.getElementById('dvh-box')) return;
  const host = document.getElementById('rtstruct-panel') || document.body;
  const box = document.createElement('div');
  box.id = 'dvh-box';
  box.style.cssText = 'margin:8px;padding:8px;border:1px solid #47a;border-radius:8px;'
    + 'background:#0b1220;color:#bdf;font:12px sans-serif;max-width:300px';
  box.innerHTML = `
    <div style="font-weight:600;margin-bottom:4px">📊 DVH (доза–объём)</div>
    <label>Структура: <select id="dvh-roi" style="max-width:150px"></select></label>
    <label style="display:block;margin-top:4px">Назначенная доза, Гр:
      <input id="dvh-rx" type="number" step="0.1" value="" style="width:70px"></label>
    <button id="dvh-run" style="width:100%;margin-top:6px;padding:6px;border-radius:6px;border:1px solid #47a;background:#0e1a2c;color:#bdf;cursor:pointer">Построить DVH</button>
    <div id="dvh-out" style="margin-top:6px"></div>`;
  host.appendChild(box);

  const sel = box.querySelector('#dvh-roi');
  const refresh = () => {
    const list = getStructuresRaw().filter(s => s.contours && s.contours.length);
    sel.innerHTML = list.map(s => `<option value="${s.roiNumber}">${s.name}</option>`).join('')
      || '<option value="">— нет структур —</option>';
    const d = getDose();
    if (d && !box.querySelector('#dvh-rx').value) box.querySelector('#dvh-rx').value = d.max.toFixed(1);
  };
  refresh();
  box.addEventListener('mouseenter', refresh);

  box.querySelector('#dvh-run').onclick = () => {
    const out = box.querySelector('#dvh-out');
    const roi = parseInt(sel.value, 10);
    const rx = parseFloat(box.querySelector('#dvh-rx').value) || null;
    out.textContent = '⏳ Считаю DVH…';
    setTimeout(() => {
      try {
        const res = computeDvhForStructure(Number.isNaN(roi) ? null : roi, rx);
        out.innerHTML = _plot(res) + _statsHtml(res);
        log.info(`[DVH] ${res.name}: V=${res.structVolCc.toFixed(2)}cc, Dmean=${res.stats.Dmean.toFixed(2)}Гр`
          + (rx ? `, V100=${(res.stats.V100pct ?? 0).toFixed(1)}%` : ''));
      } catch (e) { out.textContent = '❌ ' + e.message; log.warn('[DVH]', e.message); }
    }, 30);
  };
}

export function initDVH() {
  try {
    mountDvhUI();
    registerAPIs({ computeDvhForStructure });
    log.info('[DVH] Модуль DVH инициализирован — 1.0 (Этап 5)');
  } catch (e) { log.error('[DVH] init failed:', e); }
}

export default { initDVH, computeDvhForStructure };
